import { lazy } from 'react';
import { createHashRouter, Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import { RequireAuth } from '@/auth/RequireAuth';
import { AppShell, SessionBootstrap } from '@/components/layout';
import { DemoOnly } from '@/features/shared/DemoOnly';
import { NotFoundPage, RouteError } from '@/features/shared/NotFoundPage';
import { DELEGATE_ROUTE, PATHS } from './paths';

/**
 * Route table.
 *
 * HASH router: URLs are `#/lockerroom`, `#/agent/sr/dashboard?partner=visma` —
 * identical to the prototype, and a static build needs no server rewrite rule.
 *
 * Every screen except the Arena is lazily loaded. The prototype shipped one
 * 4k-line file; here the initial bundle carries the shell and the landing screen,
 * and each agent arrives on navigation. Agents like MMM and the activation
 * builder are the heaviest, and most sessions never open all of them.
 */

const ArenaPage = lazy(async () => ({
  default: (await import('@/features/arena/ArenaPage')).ArenaPage,
}));
const GamePage = lazy(async () => ({
  default: (await import('@/features/arena/GamePage')).GamePage,
}));
const GymPage = lazy(async () => ({
  default: (await import('@/features/arena/GymPage')).GymPage,
}));
const FrontOfficePage = lazy(async () => ({
  default: (await import('@/features/arena/FrontOfficePage')).FrontOfficePage,
}));
const LockerroomPage = lazy(async () => ({
  default: (await import('@/features/lockerroom/LockerroomPage')).LockerroomPage,
}));

const AccountPage = lazy(async () => ({
  default: (await import('@/features/account/AccountPage')).AccountPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import('@/features/settings/SettingsPage')).SettingsPage,
}));

const DataQaPage = lazy(async () => ({
  default: (await import('@/features/agents/dataqa/DataQaPage')).DataQaPage,
}));
const DqsMeasurementPage = lazy(async () => ({
  default: (await import('@/features/agents/dqsm/DqsMeasurementPage')).DqsMeasurementPage,
}));
const DqsRecommendationPage = lazy(async () => ({
  default: (await import('@/features/agents/dqsr/DqsRecommendationPage')).DqsRecommendationPage,
}));

const MmmPage = lazy(async () => ({
  default: (await import('@/features/agents/mmm/MmmPage')).MmmPage,
}));
const MmmOutputPage = lazy(async () => ({
  default: (await import('@/features/agents/mmm/MmmOutputPage')).MmmOutputPage,
}));

const ProiPage = lazy(async () => ({
  default: (await import('@/features/agents/proi/ProiPage')).ProiPage,
}));
const DelegatePage = lazy(async () => ({
  default: (await import('@/features/agents/proi/DelegatePage')).DelegatePage,
}));

const SrEntryPage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrEntryPage')).SrEntryPage,
}));
const SrConfigChoicePage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrConfigChoicePage')).SrConfigChoicePage,
}));
const SrConfigPage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrConfigPage')).SrConfigPage,
}));
const SrWizardPage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrWizardPage')).SrWizardPage,
}));
const SrDashboardPage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrDashboardPage')).SrDashboardPage,
}));
const SrPortfolioPage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrPortfolioPage')).SrPortfolioPage,
}));
const SrAdminPage = lazy(async () => ({
  default: (await import('@/features/agents/sr/SrAdminPage')).SrAdminPage,
}));

const PricingPage = lazy(async () => ({
  default: (await import('@/features/agents/pricing/PricingPage')).PricingPage,
}));

const UsagePage = lazy(async () => ({
  default: (await import('@/features/admin/UsagePage')).UsagePage,
}));

const DqsAssessmentPage = lazy(async () => ({
  default: (await import('@/features/reports/dqs/DqsAssessmentPage')).DqsAssessmentPage,
}));
const ActivationLibraryPage = lazy(async () => ({
  default: (await import('@/features/reports/activation/ActivationLibraryPage'))
    .ActivationLibraryPage,
}));
const ActivationCreatePage = lazy(async () => ({
  default: (await import('@/features/reports/activation/ActivationCreatePage'))
    .ActivationCreatePage,
}));
const ActivationBuilderPage = lazy(async () => ({
  default: (await import('@/features/reports/activation/ActivationBuilderPage'))
    .ActivationBuilderPage,
}));
const ActivationViewPage = lazy(async () => ({
  default: (await import('@/features/reports/activation/ActivationViewPage')).ActivationViewPage,
}));

export interface RouterOptions {
  /**
   * Routes mounted BESIDE the shell rather than under it: the login screen in a
   * cognito build. Nothing here is guarded and nothing here hydrates the
   * session, which is what keeps the login screen free of data reads.
   */
  readonly publicRoutes?: readonly RouteObject[];
}

/**
 * The route tree.
 *
 * Everything under `/` sits behind `RequireAuth`, and the seed hydration
 * (`SessionBootstrap`) sits behind the guard too, so no data is read before the
 * session is known. In a `none` build the guard is a pass-through and the tree
 * behaves exactly as it did when the bootstrap wrapped the router from `App`.
 */
export const buildRoutes = (options: RouterOptions = {}): RouteObject[] => [
  ...(options.publicRoutes ?? []),
  {
    path: '/',
    element: (
      <RequireAuth>
        <SessionBootstrap>
          <AppShell />
        </SessionBootstrap>
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to={PATHS.arena} replace /> },

      { path: 'arena', element: <ArenaPage /> },
      { path: 'game', element: <GamePage /> },
      { path: 'gym', element: <GymPage /> },
      { path: 'front-office', element: <FrontOfficePage /> },
      { path: 'lockerroom', element: <LockerroomPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'settings', element: <SettingsPage /> },

      { path: 'agent/dataqa', element: <DataQaPage /> },
      { path: 'agent/dqsm', element: <DqsMeasurementPage /> },
      { path: 'agent/dqsr', element: <DqsRecommendationPage /> },

      { path: 'agent/mmm', element: <MmmPage /> },
      // Gated, not removed: illustrative figures and real ones must not sit one
      // click apart in the same module (DEC-49).
      {
        path: 'agent/mmm/output',
        element: (
          <DemoOnly>
            <MmmOutputPage />
          </DemoOnly>
        ),
      },

      { path: 'agent/proi', element: <ProiPage /> },

      { path: 'agent/sr', element: <SrEntryPage /> },
      { path: 'agent/sr/config-choice', element: <SrConfigChoicePage /> },
      { path: 'agent/sr/config', element: <SrConfigPage /> },
      { path: 'agent/sr/wizard', element: <SrWizardPage /> },
      { path: 'agent/sr/dashboard', element: <SrDashboardPage /> },
      { path: 'agent/sr/portfolio', element: <SrPortfolioPage /> },
      { path: 'agent/sr/admin', element: <SrAdminPage /> },

      { path: 'agent/pricing', element: <PricingPage /> },

      { path: 'admin/usage', element: <UsagePage /> },

      { path: 'report/dqs', element: <DqsAssessmentPage /> },
      { path: 'report/activation', element: <ActivationLibraryPage /> },
      { path: 'report/activation/create', element: <ActivationCreatePage /> },
      { path: 'report/activation/build', element: <ActivationBuilderPage /> },
      { path: 'report/activation/view', element: <ActivationViewPage /> },

      { path: '*', element: <NotFoundPage /> },
    ],
  },

  // US-A11: the delegate link renders bare — no AppShell (rail/top bar). A right
  // holder opening the link is not an arena user; they see only the scoped form.
  {
    path: DELEGATE_ROUTE,
    element: <DelegatePage />,
    errorElement: <RouteError />,
  },
];

/** The route tree with no public routes, exported so tests can mount it under a different router. */
export const routes: RouteObject[] = buildRoutes();

/**
 * Builds a router bound to the CURRENT `window.location.hash`.
 *
 * A factory rather than a module-level singleton, for two reasons: a singleton
 * survives hot module replacement in dev and goes stale, and it leaks routing
 * state between tests — each test would inherit the previous one's location
 * because the instance, not the URL, holds the truth.
 */
export const createAppRouter = (options: RouterOptions = {}): ReturnType<typeof createHashRouter> =>
  createHashRouter(buildRoutes(options));
